import type { CapabilityItem, ClientDiff } from "./clients";

const lowerFirst = (text: string) =>
  text.charAt(0).toLowerCase() + text.slice(1).replace(/[.!]+$/, "");
const join = (items: string[]) =>
  new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(
    items,
  );
export const UNKNOWN_PERMISSION = "A permission this console doesn't recognise yet";
/** A catalogue title for a person: its short form (the parenthetical qualifier and full stop stay in the technical
 * details, which show the whole description), registry nouns as people say them, and personal capabilities from the
 * reviewer's side ("my devices" is the signed-in person's, not the reviewer's). */
export const permissionTitle = (title: string) =>
  title
    .replace(/\s*\([^()]*\)\s*\.?\s*$/, "")
    .replace(/\.$/, "")
    .replace(/end users up to and in to the calling client/g, "people up and sign them in to this app")
    .replace(/the calling client organisation/g, "this app's organisation")
    .replace(/the calling client/g, "this app")
    .replace(/\bmy own person\b/g, "the signed-in person's profile")
    .replace(/\bmy own\b/g, "the signed-in person's")
    .replace(/\bmy\b/g, "the signed-in person's")
    .replace(/registered clients/g, "apps")
    .replace(/a registered client/g, "an app")
    .replace(/agents' proposals/g, "approvals")
    .replace(/proposals/g, "approvals")
    .replace(/a proposal/g, "an approval")
    .replace(/proposal/g, "approval");

/** Catalogue titles are verb phrases, never operation summaries or machine identifiers. */
export function permissionPhrase(
  permissions: string[],
  catalogue: CapabilityItem[],
) {
  return join(
    permissions.map((capability) => {
      const item = catalogue.find((entry) => entry.capability === capability);
      return item
        ? lowerFirst(permissionTitle(item.title))
        : "use a permission this console doesn't recognise yet";
    }),
  );
}

export function permissionConsequence(
  name: string,
  diff: ClientDiff,
  catalogue: CapabilityItem[],
) {
  const clauses: string[] = [];
  if (diff.added.length)
    clauses.push(`wants to ${permissionPhrase(diff.added, catalogue)}`);
  if (diff.removed.length)
    clauses.push(`will no longer ${permissionPhrase(diff.removed, catalogue)}`);
  if (diff.redirectsAdded.length)
    clauses.push("wants to add a sign-in return address");
  if (diff.redirectsRemoved.length)
    clauses.push("will stop using a sign-in return address");
  if (diff.keysChanged) clauses.push("wants to change its public keys");
  if (diff.nameChanged) clauses.push("wants to change its name");
  return `${name} ${join(clauses) || "will keep its current permissions"}`;
}

/** A compact overview, with the complete catalogue sentences on the detail screen. */
export function appPermissionSummary(
  permissions: string[],
  catalogue: CapabilityItem[],
) {
  if (!permissions.length) return "No permissions";
  const titles = permissions.slice(0, 2).map((capability) => {
    const item = catalogue.find((entry) => entry.capability === capability);
    if (!item) return UNKNOWN_PERMISSION;
    if (item.assignable === false) return permissionTitle(item.title);
    // Inflect only a known leading verb; retain every catalogue noun and qualifier.
    return permissionTitle(item.title).replace(
      /^(Read|Create|Confirm|Manage|List|Update|Delete|Send|View|Register|Revoke|Change|Get|Set|Access|Download|Upload)\b/,
      (verb) => ({ Access: "Accesses" })[verb] ?? `${verb}s`,
    );
  });
  return (
    titles.join("; ") +
    (permissions.length > 2 ? `; +${permissions.length - 2} more` : "")
  );
}
